import * as React from "react";
import { observer, inject } from "mobx-react";

import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";

import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { Travelers } from "components/shared/Travelers/Travelers";

import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";

import { NoRehydratationValues } from "components/shared/TravelersField/typings";
import { WizardHotelPWAv2Props, WizardHotelPWAv2FTNodeOptions } from "../../typings";
import { locKeys } from "components/flexComponents/WizardHotelPWA/components/l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { RateCalendarFooter } from "components/flexComponents/WizardHotelPWA/components/RateCalendarFooter";
import { CheckboxModuleNameTypes } from "src/components/shared/PropertyFIlterComponents/components/Checkbox";

import { RateCalendarStore } from "src/stores/rateCalendar";
import { SubmitButton } from "src/components/shared/SubmitButton/SubmitButton";
import { HotelHiddenSearchData } from "src/components/flexComponents/WizardHotelPWAv2/components/HotelHiddenSearchData/HotelHiddenSearchData";
import { InputMapper } from "src/components/flexComponents/WizardHotelPWAv2/components/HotelHiddenSearchData/typings";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import { normalizeOptionsData } from "src/stores/FlexTemplateStore";
import { UitkSpacing } from "uitk-react-spacing";
import { useStickyElement } from "components/shared/StackedSticky/StackedSticky";

enum DateState {
  START = "startDate",
  END = "endDate",
}

const STATIC_INPUTS: InputMapper = {
  rfrr: {
    values: ["hotel.search"],
  },
  GOTO: {
    values: ["HOTSEARCH"],
  },
  SearchType: {
    values: ["Place"],
  },
  SearchArea: {
    values: ["City"],
  },
  needUTF8Decode: {
    values: ["true"],
  },
};

export interface WizardFormProps
  extends Pick<WizardHotelPWAv2Props, "flexTemplateNode" | "wizardState" | "analytics" | "context"> {
  rateCalendar?: RateCalendarStore;
  isDesktop: boolean;
  flexModuleModelStore: FlexModuleModelStore;
  desktopAutoOpenCalendar?: boolean;
}

export const WizardForm: React.FC<WizardFormProps> = inject("rateCalendar")(
  observer((props) => {
    const {
      context,
      wizardState,
      flexTemplateNode,
      rateCalendar,
      isDesktop,
      flexModuleModelStore,
      desktopAutoOpenCalendar,
    } = props;
    const {
      globalWizardState,
      hotelWizardState,
      hotelWizardState: { config, numberOfErrors, errorInputRef, moreThanOneError },
    } = wizardState;

    const { elementId, showDestinationField } = config;

    const { formatText } = useLocalization();

    const model = flexModuleModelStore.getModel(flexTemplateNode.id);
    const options = normalizeOptionsData(flexTemplateNode, model) as WizardHotelPWAv2FTNodeOptions;

    const mainTitleWizardClassNames: UitkLayoutConditionalGridSpan = { medium: 12, large: 12 };
    const cardColSpan: UitkLayoutConditionalGridSpan = { medium: 4, large: 12 };
    const mainGridColumns: UitkLayoutConditionalGridColumnRow = { medium: 4, large: 8 };
    const locationColSpan: UitkLayoutConditionalGridSpan = { medium: 4, large: 2 };
    const datesColSpan: UitkLayoutConditionalGridSpan = { medium: 2, large: 3 };
    const travelersColSpan: UitkLayoutConditionalGridSpan = { medium: 1, large: 2 };
    const submitColSpan: UitkLayoutConditionalGridSpan = { medium: 1, large: 1 };

    const [htgStartInvalidMessage, setStartInvalidMessage] = React.useState<string>("");
    const [htgEndInvalidMessage, setEndInvalidMessage] = React.useState<string>("");

    const { isSticky } = useStickyElement();

    const [datePickerIsOpen, setDatePickerIsOpen] = React.useState(Boolean(isDesktop && desktopAutoOpenCalendar));

    React.useEffect(() => {
      // this prevent the new date picker to stay opened when switching between sticky/non-sticky wizard
      if (isSticky) {
        setDatePickerIsOpen(false);
      }
    }, [isSticky]);

    const toggleDatePicker = () => {
      setDatePickerIsOpen(!datePickerIsOpen);
    };

    if (!hotelWizardState.regionId) {
      hotelWizardState.setRegionId(context.searchContext.location.id);
    }

    /**
     * Values used if no rehydratation(JS disabled)
     */
    const noRehydratationValues: NoRehydratationValues = {
      numOfTravelers: 2,
      name: "adults",
    };

    const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
      hotelWizardState.wizardInputsArray.push(ref);
      if (secondRef) {
        hotelWizardState.wizardInputsArray.push(secondRef);
      }
    };

    const getLocError = (isLink: boolean) => {
      if (!isLink) {
        return hotelWizardState.moreThanOneError()
          ? config.form.submitButton.locMultipleWarningsToken
          : config.form.submitButton.locWarningToken;
      }

      return hotelWizardState.moreThanOneError()
        ? config.form.submitButton.locMultipleWarningsLinkToken
        : config.form.submitButton.locWarningLinkToken;
    };

    const isValidDate = (date: Date) => {
      return date && date.toString() !== "Invalid Date";
    };

    const validate = (key: string) => {
      const checkInDate = new Date(hotelWizardState.startDate);
      const checkOutDate = new Date(hotelWizardState.endDate);

      if (key === DateState.START && !isValidDate(checkInDate)) {
        return formatText(locKeys.invalidFromDate);
      }

      if (key === DateState.END && !isValidDate(checkOutDate)) {
        return formatText(locKeys.invalidToDate);
      }

      if (key === DateState.END && isValidDate(checkInDate)) {
        const maxEndDate = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate() + 28);

        if (checkOutDate < checkInDate) {
          return formatText(locKeys.invalidDateRangeEndBeforeStart);
        }

        if (checkOutDate > maxEndDate) {
          return formatText(locKeys.invalidDateRangeEndOverLimit);
        }
      }

      return "";
    };

    const validateAndGetFormErrors = (event?: React.FormEvent<HTMLFormElement>) => {
      const errors = [];
      const startDateError = validate(DateState.START);
      const endDateError = validate(DateState.END);

      setStartInvalidMessage(startDateError);
      setEndInvalidMessage(endDateError);

      if (startDateError) {
        errors.push(startDateError);
      }

      if (endDateError) {
        errors.push(endDateError);
      }

      if (errors.length > 0 && event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      return errors;
    };

    const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
      if (validateAndGetFormErrors().length > 0) {
        e.preventDefault();
      }
    };

    const submitForm = () => {
      if (validateAndGetFormErrors().length === 0) {
        wizardFormRef?.current?.submit();
      }
    };

    const wizardFormRef = React.useRef<HTMLFormElement>(null);

    /* istanbul ignore next */
    const rateCalendarProps = rateCalendar?.isExperimentEnabled
      ? {
          datePickerContent: rateCalendar.datePickerContent,
          footerHtml: <RateCalendarFooter {...rateCalendar.footerHtmlProps} />,
          footerText: rateCalendar.footerText,
        }
      : {};

    return (
      <UitkLayoutGrid columns={mainGridColumns}>
        <UitkLayoutGridItem colSpan={cardColSpan}>
          <UitkSpacing padding={{ medium: { inline: "three", block: isSticky ? "three" : "six" } }}>
            <form
              noValidate
              id={elementId}
              data-fm={options.fmId}
              data-fm-title-id={options.fmTitleId}
              onSubmit={onSubmitForm}
              action={hotelWizardState.formAction}
              ref={wizardFormRef}
            >
              <UitkLayoutGrid columns={mainGridColumns} space="three">
                {numberOfErrors > 0 && (
                  <LobErrorSummary
                    locHeadingToken={getLocError(false)!}
                    locHeadingArgs={moreThanOneError() && [numberOfErrors]}
                    locLinkToken={getLocError(true)}
                    linkClickFocusId="roundtrip-error"
                    inputErrorRef={errorInputRef}
                    colSpan={mainGridColumns}
                    setInputsRefs={setInputsRefs}
                  />
                )}
                {isSticky && isDesktop && (
                  <UitkLayoutGridItem colSpan={mainTitleWizardClassNames}>
                    <h1 className="uitk-type-500 is-visually-hidden">{formatText(locKeys.stickyWizardTitle)}</h1>
                  </UitkLayoutGridItem>
                )}
                {showDestinationField && (
                  <Location
                    originField={false}
                    destinationField
                    lobState={hotelWizardState}
                    colSpan={locationColSpan}
                    setInputsRefs={setInputsRefs}
                    globalConfig={globalWizardState.config}
                  />
                )}
                <Dates
                  lobState={hotelWizardState}
                  colSpan={datesColSpan}
                  setInputsRefs={setInputsRefs}
                  autoOpenCalendar={datePickerIsOpen}
                  toggleDatePicker={toggleDatePicker}
                  isDesktop={isDesktop}
                  {...rateCalendarProps}
                  htgStartInvalidMessage={htgStartInvalidMessage}
                  htgEndInvalidMessage={htgEndInvalidMessage}
                  validateAndGetFormErrors={validateAndGetFormErrors}
                  submitForm={submitForm}
                  hasEmptyDates={true}
                />

                <Travelers
                  lobState={hotelWizardState}
                  colSpan={travelersColSpan}
                  noRehydratationValues={noRehydratationValues}
                  submitForm={submitForm}
                />

                <SubmitButton
                  lobState={hotelWizardState}
                  colSpan={submitColSpan}
                  rfrr="SearchClick_Hotel"
                  globalConfig={globalWizardState.config}
                  moduleName={CheckboxModuleNameTypes.WIZARD_HOTEL_V2_ROW}
                  testId="wizard-submit-button"
                />
              </UitkLayoutGrid>
              <HotelHiddenSearchData
                hotelWizardState={hotelWizardState}
                staticInputs={{
                  ...STATIC_INPUTS,
                  lang: {
                    isVisible: Boolean(context.langId),
                    values: [String(context.langId)],
                  },
                }}
              />
            </form>
          </UitkSpacing>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );
  })
);

export default WizardForm;
